import { readFile } from 'node:fs/promises';
import { resolve } from 'node:path';
import type { RedmineConfig } from '../types/redmine.types.js';
import { RedmineConfigSchema } from '../types/redmine.types.js';
import { parse } from 'yaml';
import { z } from 'zod';
import { ValidationError, NotFoundError } from '../errors/index.js';

/**
 * Service for managing Redmine configuration
 */
export class RedmineConfigService {
    private configPath: string;

    constructor(configPath?: string) {
        this.configPath = configPath ?? resolve(process.cwd(), 'redmine.config.yaml');
    }

    /**
     * Load Redmine configuration from YAML file
     */
    async load(): Promise<RedmineConfig> {
        try {
            const content = await readFile(this.configPath, 'utf-8');
            const rawConfig = parse(content);

            return RedmineConfigSchema.parse(rawConfig);
        } catch (error) {
            if (error instanceof z.ZodError) {
                throw new ValidationError(`Configuration validation failed: ${error.message}`);
            }

            if (error instanceof Error && 'code' in error && error.code === 'ENOENT') {
                throw new NotFoundError(`Configuration file not found: ${this.configPath}`);
            }

            throw new ValidationError(
                `Failed to load configuration: ${error instanceof Error ? error.message : String(error)}`
            );
        }
    }

    /**
     * Validate configuration object
     */
    validate(config: unknown): RedmineConfig {
        try {
            return RedmineConfigSchema.parse(config);
        } catch (error) {
            if (error instanceof z.ZodError) {
                throw new ValidationError(`Configuration validation failed: ${error.message}`);
            }
            throw error;
        }
    }

    /**
     * Get config file path
     */
    getConfigPath(): string {
        return this.configPath;
    }
}

// Export schema for use in types file
export { RedmineConfigSchema };
